import { useI18n } from '@i18n';
import { Stack, SxProps, TextFieldProps, Theme } from '@mui/material';
import { language_enum } from '@predium/enums';
import { Controller, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { NumberFormatProps, NumberFormatValues, SourceInfo } from 'react-number-format';
import { PInputState } from '@/components/atoms/inputState';
import { PNumberField } from '@/components/atoms/NumberField/PNumberField';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { applyWarningSx, parseStringToNumber } from '@/components/hook-form/utils';
import WarningHelperText from '@/components/hook-form/WarningHelperText';
import { mergeSx } from '@/utils/cssStyles';

type IProps<TFieldValues extends FieldValues = FieldValues> = {
  name: FieldPath<TFieldValues>;
  label?: string;
  valueAsText?: boolean;
  min?: number;
  max?: number;
  size?: TextFieldProps['size']; // Update the type declaration for size
  sx?: SxProps<Theme>;
  state?: PInputState;
  onBlur?: () => void;
  onFocus?: () => void;
  onEnter?: () => void;
  onValueChange?: (values: NumberFormatValues, sourceInfo: SourceInfo) => void;
  variant?: TextFieldProps['variant'];
  placeholder?: string;
  decimalScale?: number;
  thousandSeparator?: string;
  decimalSeparator?: string;
  suffix?: string;
  helperText?: string;
  nullable?: boolean;
  autoFocus?: boolean;
  numberFormatProps?: NumberFormatProps<TextFieldProps>;
  allowDecimals?: boolean;
  maxDecimalPlaces?: number;
  autoComplete?: TextFieldProps['autoComplete'];
  allowLeadingZeros?: boolean;
  allowNegative?: boolean;
  showError?: boolean;
  warningText?: string;
};

export type RHFNumberFieldProps<TFieldValues extends FieldValues = FieldValues> = IProps<TFieldValues> &
  Pick<TextFieldProps, 'InputProps' | 'onChange'>;

export default function RHFNumberField<TFieldValues extends FieldValues = FieldValues>({
  name,
  valueAsText,
  size = 'medium',
  state,
  min,
  max,
  sx,
  onBlur,
  onFocus,
  onEnter,
  variant,
  placeholder,
  helperText,
  nullable,
  onValueChange,
  numberFormatProps = {},
  allowDecimals = true,
  autoComplete = 'on',
  maxDecimalPlaces = 3,
  allowLeadingZeros = false,
  allowNegative = false,
  showError = true,
  warningText,
  ...other
}: RHFNumberFieldProps<TFieldValues>) {
  const { control } = useFormContext<TFieldValues>();
  const { language } = useI18n();
  const baseSx = mergeSx({ maxWidth: '100%' }, sx);
  const effectiveSx = applyWarningSx(warningText, baseSx);
  const decimalSeparator = language === language_enum.deDE ? ',' : '.';
  const thousandSeparator = language === language_enum.deDE ? '.' : ',';

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...field }, fieldState: { error } }) => {
        const handleValueChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
          if (nullable && values.value === '') {
            field.onChange(null);
          } else if (valueAsText) {
            field.onChange(values.value);
          } else {
            field.onChange(values.floatValue ?? null);
          }
          onValueChange?.(values, sourceInfo);
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter' || e.key === 'Escape') {
            onBlur?.(); // Trigger onBlur on Enter key press
            onEnter?.();
            e.preventDefault();
            e.stopPropagation();
          }
        };

        const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
          e.preventDefault();
          const pasteData = e.clipboardData.getData('Text').trim();

          if (!pasteData) return;

          const numberValue = parseStringToNumber({ value: pasteData, maxDecimalPlaces });

          if (numberValue === undefined) return;

          // Update the field with the correctly formatted number
          field.onChange(numberValue);
        };

        const onChange = (_: React.ChangeEvent<HTMLInputElement>) => {
          //intentionally left blank to prevent input field to set the value as string
          //This won't work with German formatting, so we use onValueChange instead
        };

        return (
          <PNumberField
            //isNumericString is necessary for when the language is in german, otherwise the decimalSeparator won't work as expected
            isNumericString
            allowNegative={allowNegative}
            allowLeadingZeros={allowLeadingZeros}
            allowedDecimalSeparators={allowDecimals ? [',', '.'] : undefined}
            decimalSeparator={allowDecimals ? decimalSeparator : undefined}
            thousandSeparator={allowDecimals ? thousandSeparator : false}
            decimalScale={allowDecimals ? maxDecimalPlaces : 0} // Default to 3 decimal places if decimals are allowed
            inputRef={ref}
            variant={variant}
            color="primary"
            fullWidth
            size={size}
            error={!!error}
            helperText={
              error?.message && showError ? (
                <Stack>
                  <PTypography variant="caption" color="error">
                    {error.message}
                  </PTypography>
                  {helperText ? (
                    <PTypography variant="caption" color="secondary">
                      {helperText}
                    </PTypography>
                  ) : null}
                </Stack>
              ) : warningText ? (
                <WarningHelperText text={warningText} />
              ) : (
                helperText
              )
            }
            sx={effectiveSx}
            state={state}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={placeholder}
            autoComplete={autoComplete}
            isAllowed={(values) => {
              const floatValue = values?.floatValue;

              // Fix: Always allow empty values (when user clears the field) regardless of min/max constraints
              // This ensures backspace clearing works correctly even when min is defined
              if (floatValue === undefined) {
                return true;
              }

              // If no min/max constraints, allow any value
              if (typeof min !== 'number' && typeof max !== 'number') {
                return true;
              }

              // Check if value is within min/max range
              const isAboveMin = typeof min !== 'number' || floatValue >= min;
              const isBelowMax = typeof max !== 'number' || floatValue <= max;

              return isAboveMin && isBelowMax;
            }}
            {...numberFormatProps}
            {...field}
            {...other}
            onBlur={onBlur}
            onFocus={onFocus}
            onChange={onChange}
            onValueChange={handleValueChange}
          />
        );
      }}
    />
  );
}
